// ─────────────────────────────────────────────────────────────────────────────
// priceUpdateExport.js
//
// Turns price-update audit logs (GET /item-database/price-updates/:id) into an
// Excel workbook or CSV that can be filed away or handed to estimators for
// import into other software.
//
//   Sheet 1  "Price Updates"  – one flat row per item we changed, plain header
//                               row, real numbers, ISO dates (import friendly)
//   Sheet 2  "Summary"        – totals + one line per run
//   Sheet 3  "Not Applied"    – only if something was rejected
//
// CSV export is Sheet 1 only.
// ─────────────────────────────────────────────────────────────────────────────
import * as XLSX from "xlsx";
import axios from "axios";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const MATCH_LABEL = {
  sku: "SKU match",
  ai: "Description match (AI)",
  text: "Description match (text)",
};

const r6 = (n) =>
  n === null || n === undefined || n === ""
    ? ""
    : Math.round(Number(n) * 1e6) / 1e6;

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => {
  const x = new Date(d);
  return isNaN(x)
    ? ""
    : `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};
const ymdhm = (d) => {
  const x = new Date(d);
  return isNaN(x)
    ? ""
    : `${ymd(x)} ${pad(x.getHours())}:${pad(x.getMinutes())}`;
};

// Column order of the main sheet (header text is what other software sees)
const HEADERS = [
  "Vendor",
  "Vendor SKU",
  "Item Code",
  "Description",
  "Size",
  "UOM",
  "Previous Price",
  "New Price",
  "Change ($)",
  "Change (%)",
  "Vendor Price",
  "Vendor Unit",
  "Previous SKU",
  "SKU Changed",
  "New Vendor Entry",
  "Match Type",
  "Confidence (%)",
  "Date Applied",
  "Applied By",
  "Source File",
];
const PRICE_COLS = [
  "Previous Price",
  "New Price",
  "Change ($)",
  "Vendor Price",
];
const PCT_COLS = ["Change (%)", "Confidence (%)"];

const rowFor = (log, e) => ({
  Vendor: log.vendorName || "",
  "Vendor SKU": e.vendorSku || "",
  "Item Code": e.itemCode || "",
  Description: e.description || "",
  Size: e.size || "",
  UOM: e.uom || "",
  "Previous Price": r6(e.previousPrice),
  "New Price": r6(e.newPrice),
  "Change ($)": r6(e.change),
  "Change (%)":
    e.changePct === null || e.changePct === undefined ? "" : e.changePct,
  "Vendor Price": r6(e.vendorPrice),
  "Vendor Unit": e.vendorUnit || "",
  "Previous SKU": e.previousSku || "",
  "SKU Changed": e.skuChanged ? "Yes" : "No",
  "New Vendor Entry": e.vendorEntryAdded ? "Yes" : "No",
  "Match Type": MATCH_LABEL[e.matchType] || e.matchType || "",
  "Confidence (%)":
    e.confidence === null || e.confidence === undefined ? "" : e.confidence,
  "Date Applied": ymd(log.appliedAt),
  "Applied By": log.appliedByName || "",
  "Source File": log.fileName || "",
});

/** Split logs into flat applied / not-applied rows. */
export function flattenLogs(logs) {
  const applied = [];
  const notApplied = [];
  for (const log of logs) {
    for (const e of log.entries || []) {
      if (e.status === "failed") {
        notApplied.push({ ...rowFor(log, e), Reason: e.error || "" });
      } else {
        applied.push(rowFor(log, e));
      }
    }
  }
  return { applied, notApplied };
}

const autoWidths = (rows, headers) =>
  headers.map((h) => ({
    wch: Math.min(
      50,
      Math.max(h.length + 2, ...rows.map((r) => String(r[h] ?? "").length + 2)),
    ),
  }));

function styleSheet(ws, headers) {
  const range = XLSX.utils.decode_range(ws["!ref"] || "A1:A1");
  headers.forEach((h, c) => {
    const fmt = PRICE_COLS.includes(h)
      ? "0.0000"
      : PCT_COLS.includes(h)
        ? "0.00"
        : null;
    if (!fmt) return;
    for (let r = range.s.r + 1; r <= range.e.r; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && cell.t === "n") cell.z = fmt;
    }
  });
  ws["!autofilter"] = { ref: ws["!ref"] };
}

export function buildPriceUpdateWorkbook(logs) {
  const { applied, notApplied } = flattenLogs(logs);
  const wb = XLSX.utils.book_new();

  // 1 ── main sheet
  const ws1 = XLSX.utils.json_to_sheet(applied, { header: HEADERS });
  ws1["!cols"] = autoWidths(applied, HEADERS);
  styleSheet(ws1, HEADERS);
  XLSX.utils.book_append_sheet(wb, ws1, "Price Updates");

  // 2 ── summary
  const sum = (f) => logs.reduce((s, l) => s + (l.counts?.[f] || 0), 0);
  const vendors = [...new Set(logs.map((l) => l.vendorName).filter(Boolean))];
  const dates = logs.map((l) => new Date(l.appliedAt)).filter((d) => !isNaN(d));
  const first = dates.length ? new Date(Math.min(...dates)) : null;
  const last = dates.length ? new Date(Math.max(...dates)) : null;

  const aoa = [
    ["Vendor Price Updates"],
    [],
    ["Vendor(s)", vendors.join(", ")],
    ["Runs", logs.length],
    ["From", first ? ymdhm(first) : ""],
    ["To", last ? ymdhm(last) : ""],
    ["Items updated", sum("applied")],
    ["Price increases", sum("increases")],
    ["Price decreases", sum("decreases")],
    ["SKUs changed", sum("skuChanged")],
    ["New vendor entries added", sum("vendorEntriesAdded")],
    ["Not applied", sum("failed")],
    [],
    [
      "Date",
      "Vendor",
      "Source File",
      "Applied By",
      "Updated",
      "Increases",
      "Decreases",
      "SKUs Changed",
      "Entries Added",
    ],
    ...[...logs]
      .sort((a, b) => new Date(a.appliedAt) - new Date(b.appliedAt))
      .map((l) => [
        ymdhm(l.appliedAt),
        l.vendorName || "",
        l.fileName || "",
        l.appliedByName || "",
        l.counts?.applied || 0,
        l.counts?.increases || 0,
        l.counts?.decreases || 0,
        l.counts?.skuChanged || 0,
        l.counts?.vendorEntriesAdded || 0,
      ]),
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(aoa);
  ws2["!cols"] = [
    { wch: 26 },
    { wch: 24 },
    { wch: 34 },
    { wch: 18 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
    { wch: 13 },
    { wch: 13 },
  ];
  XLSX.utils.book_append_sheet(wb, ws2, "Summary");

  // 3 ── rejected rows (only when there are any)
  if (notApplied.length) {
    const h3 = [...HEADERS, "Reason"];
    const ws3 = XLSX.utils.json_to_sheet(notApplied, { header: h3 });
    ws3["!cols"] = autoWidths(notApplied, h3);
    XLSX.utils.book_append_sheet(wb, ws3, "Not Applied");
  }

  return wb;
}

export function exportFileName(logs, ext) {
  const vendors = [...new Set(logs.map((l) => l.vendorName).filter(Boolean))];
  const slug =
    vendors.length === 1
      ? vendors[0]
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "")
      : "multiple-vendors";
  return `price-updates_${slug}_${ymd(new Date())}.${ext}`;
}

export function downloadPriceUpdateXlsx(logs) {
  const wb = buildPriceUpdateWorkbook(logs);
  XLSX.writeFile(wb, exportFileName(logs, "xlsx"));
}

export function downloadPriceUpdateCsv(logs) {
  const wb = buildPriceUpdateWorkbook(logs);
  XLSX.writeFile(wb, exportFileName(logs, "csv"), { bookType: "csv" });
}

/** Load full logs (with entries) for the given ids. */
export async function fetchPriceUpdateLogs(ids) {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const out = [];
  for (const id of ids) {
    const res = await axios.get(
      `${API_BASE}/item-database/price-updates/${id}`,
      {
        headers,
      },
    );
    if (res.data?.log) out.push(res.data.log);
  }
  return out;
}
